/* FreeTierGate — wraps a puzzle page for free-tier users.
 *
 * Behaviour (2026-06, daily cap + rewarded-ad unlock):
 *   1. GET /api/ads/unlock-status?puzzle_type=...&language_slug=...
 *      (allowance resets to 3 plays each day, UTC — server-side)
 *   2. If locked (today's plays used) → wall card: watch 3 partner ads to
 *      earn 3 more plays today, or upgrade for unlimited ad-free play.
 *   3. If not locked → POST /api/ads/consume-play, then render children.
 *
 * Subscribers and not-logged-in users bypass the gate (the backend returns
 * free_tier: false or the route is already protected by RequireAuth for the
 * non-free puzzle types).
 */
import React, { useEffect, useState, useCallback } from 'react';
import { Link, useParams } from 'react-router-dom';
import { api } from '@/lib/api';
import { useAuth } from '@/contexts/AuthContext';
import { Sparkles, Lock, Play } from 'lucide-react';
import RewardedAdModal from '@/components/RewardedAdModal';

const FREE_PUZZLE_NAMES = {
    word_search: 'Word Search',
    balloon_pop: 'Balloon Pop',
    crossword: 'Crossword',
    word_scramble: 'Word Scramble',
    missing_vowel: 'Missing Vowel',
    cryptogram: 'Cryptogram',
};

function Paywall({ puzzleName, adsRequired, adsAlreadyWatched, onWatchAds }) {
    return (
        <main className="max-w-2xl mx-auto px-6 py-16" data-testid="paywall-card">
            <div className="bg-white border border-lq-ink-100 rounded-3xl p-8">
                <div className="inline-flex items-center justify-center w-12 h-12 rounded-2xl bg-lq-purple-100 text-lq-purple-700">
                    <Lock size={22} />
                </div>
                <h1 className="mt-4 font-display text-3xl md:text-4xl font-semibold text-lq-ink-black">You&rsquo;ve used today&rsquo;s free plays.</h1>
                <p className="mt-3 text-lq-ink-700 leading-relaxed">
                    Free families get three free plays on {puzzleName} each day. Watch {adsRequired} short adverts to earn three more plays today &mdash; or go unlimited and advert-free with a paid plan.
                </p>
                <ul className="mt-4 space-y-1.5 text-sm text-lq-ink-900">
                    <li className="flex items-start gap-2"><span className="text-lq-purple-500 mt-0.5">✓</span> Unlimited plays on all six puzzle types</li>
                    <li className="flex items-start gap-2"><span className="text-lq-purple-500 mt-0.5">✓</span> All six languages unlocked (Shona, Ndebele, Yoruba, Swahili, French, Portuguese)</li>
                    <li className="flex items-start gap-2"><span className="text-lq-purple-500 mt-0.5">✓</span> Family leaderboard &middot; parents play and earn points too</li>
                    <li className="flex items-start gap-2"><span className="text-lq-purple-500 mt-0.5">✓</span> 100% advert-free, on every page &middot; 7-day free trial</li>
                </ul>
                <div className="mt-7 flex flex-wrap gap-3">
                    <Link to="/pricing" className="inline-flex items-center gap-2 bg-lq-purple-500 hover:bg-lq-purple-700 text-white font-semibold px-6 py-3 rounded-full" data-testid="paywall-cta-pricing">
                        Go unlimited &amp; ad-free
                    </Link>
                    <button onClick={onWatchAds} className="inline-flex items-center gap-2 bg-white border-2 border-lq-ink-100 hover:border-lq-purple-300 text-lq-ink-900 font-semibold px-6 py-3 rounded-full" data-testid="paywall-cta-watch-ads">
                        <Play size={16}/> Watch {adsRequired - adsAlreadyWatched} short {adsRequired - adsAlreadyWatched === 1 ? 'ad' : 'ads'} &rarr; 3 more plays
                    </button>
                </div>
                <p className="mt-5 text-xs text-lq-ink-500">
                    Plays reset every day. Got a voucher? <Link to="/redeem" className="text-lq-purple-700 hover:underline">Redeem it here.</Link>
                </p>
            </div>
        </main>
    );
}

export default function FreeTierGate({ puzzleType, children }) {
    const { user } = useAuth();
    const { language } = useParams();
    const [status, setStatus] = useState(null);
    const [consumed, setConsumed] = useState(false);
    const [showAds, setShowAds] = useState(false);

    const refresh = useCallback(async () => {
        if (!user) { setStatus({ free_tier: false, locked: false }); return; }
        try {
            const { data } = await api.get('/ads/unlock-status', {
                params: { puzzle_type: puzzleType, language_slug: language },
            });
            setStatus(data);
        } catch (e) {
            setStatus({ free_tier: false, locked: false });
        }
    }, [puzzleType, language, user]);

    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (!status || !status.free_tier || status.locked || consumed) return;
        let mounted = true;
        (async () => {
            try {
                const { data } = await api.post('/ads/consume-play', {
                    puzzle_type: puzzleType, language_slug: language,
                });
                if (mounted) {
                    setConsumed(true);
                    setStatus((s) => ({ ...s, plays_remaining: data.plays_remaining, locked: data.locked }));
                }
            } catch {
                if (mounted) setConsumed(true);
            }
        })();
        return () => { mounted = false; };
    }, [status, consumed, puzzleType, language]);

    if (!status) return null;

    if (status.free_tier && status.locked) {
        const name = FREE_PUZZLE_NAMES[puzzleType] || 'this puzzle';
        return (
            <>
                <Paywall
                    puzzleName={name}
                    adsRequired={status.ads_required_to_unlock || 3}
                    adsAlreadyWatched={status.ads_watched_in_cycle || 0}
                    onWatchAds={() => setShowAds(true)}
                />
                {showAds && (
                    <RewardedAdModal
                        puzzleType={puzzleType}
                        languageSlug={language}
                        puzzleName={name}
                        adsRequired={status.ads_required_to_unlock || 3}
                        adsAlreadyWatched={status.ads_watched_in_cycle || 0}
                        onClose={() => setShowAds(false)}
                        onUnlocked={(data) => {
                            setShowAds(false);
                            setConsumed(false);
                            setStatus((s) => ({ ...s, locked: false, plays_remaining: data.plays_remaining, ads_watched_in_cycle: 0 }));
                        }}
                    />
                )}
            </>
        );
    }

    return (
        <>
            {status.free_tier && status.plays_remaining > 0 && (
                <div className="max-w-4xl mx-auto px-6 pt-6">
                    <div className="inline-flex items-center gap-2 text-xs text-lq-ink-700 bg-lq-peach-100 border border-lq-peach-300 rounded-full px-3 py-1" data-testid="free-tier-plays-badge">
                        <Sparkles size={12} className="text-lq-peach-600"/>
                        {status.plays_remaining} free {status.plays_remaining === 1 ? 'play' : 'plays'} left today &middot; <Link to="/pricing" className="text-lq-purple-700 hover:underline ml-1">go unlimited</Link>
                    </div>
                </div>
            )}
            {children}
        </>
    );
}
